import { useEffect } from 'react';

/** Tables that keep their desktop layout on phones (POS carts, editable line grids). */
const SKIP_SELECTOR = [
  '.ordered-items-table',
  '.maximize-ordered-items-table',
  '.rt-no-cards',
].join(',');

function headerLabels(table) {
  const headRows = table.tHead?.rows;
  if (!headRows || headRows.length === 0) return null;
  const labels = [];
  Array.from(headRows[headRows.length - 1].cells).forEach((cell) => {
    const text = (cell.getAttribute('aria-label') || cell.textContent || '')
      .replace(/[↑↓▲▼⇅]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const span = Math.max(1, cell.colSpan || 1);
    for (let i = 0; i < span; i += 1) labels.push(text);
  });
  return labels;
}

function labelTable(table) {
  if (table.matches(SKIP_SELECTOR) || table.closest('.rt-no-cards')) return;
  const labels = headerLabels(table);
  if (!labels) return;

  table.classList.add('rt-cards');
  Array.from(table.tBodies).forEach((tbody) => {
    Array.from(tbody.rows).forEach((row) => {
      const cells = Array.from(row.cells);
      const isFullRow = cells.length === 1 && (cells[0].colSpan || 1) > 1;
      row.classList.toggle('rt-full-row', isFullRow);
      if (isFullRow) return;

      let col = 0;
      cells.forEach((cell) => {
        const label = labels[col] ?? '';
        if (cell.getAttribute('data-label') !== label) cell.setAttribute('data-label', label);
        col += Math.max(1, cell.colSpan || 1);
      });
    });
  });
}

/**
 * Copies each table's column headers onto its body cells as `data-label`, so CSS can
 * render rows as labelled cards on small screens without touching every page.
 */
export default function useResponsiveTableLabels(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof MutationObserver === 'undefined') return undefined;

    let frame = 0;
    const run = () => {
      frame = 0;
      root.querySelectorAll('table').forEach(labelTable);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(run);
    };

    run();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [rootRef]);
}
